import { useIsMobile } from "@/hooks/useIsMobile";
import useWindowSize from "@/hooks/useWindowSize";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import Link from "next/link";
import { useRef } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode, Mousewheel, Keyboard, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

const Carousel = ({
  children,
  title,
  showMoreText,
  showMoreLink,
  spaceBetween = 16,
  breakpoints,
  type,
  hideTitle,
  actions,
  totalAmount,
  flushTitle,
  container = true,
  loop = false,
  autoplay = false,
}) => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const isMobile = useIsMobile();

  return (
    <div className={`${container ? "md:container" : ""}`}>
      {/* === Title Section === */}
      {title && !hideTitle && (
        <div
          className={`${
            container ? "px-4 md:px-0" : flushTitle ? "px-4 lg:px-0" : "px-4"
          } flex items-center justify-between space-x-5 leading-none text-white lg:justify-normal`}
        >
          <span className='inline-flex items-center gap-2 lg:gap-2.5 text-xl font-medium leading-none text-white md:text-2xl lg:text-3xl font-display'>
            <span className='-translate-y-0.5 lg:-translate-y-1'>{title}</span>

            {totalAmount && (
              <span className='inline-flex items-center justify-center font-sans text-[10px] md:text-xs font-semibold rounded-full px-2 py-1 pb-1.5 leading-none bg-white/10'>
                {totalAmount}
              </span>
            )}
          </span>

          <span className='flex-1 hidden h-px lg:block bg-gradient-to-r from-yellow-1 to-red-1'></span>

          {actions && actions()}

          {showMoreLink && (
            <Link
              href={showMoreLink}
              className='inline-flex items-center gap-2 text-xs font-medium text-white/40 lg:text-white lg:text-sm'
            >
              <span>{showMoreText ?? "View All"}</span>
              <CaretRight />
            </Link>
          )}
        </div>
      )}

      {/* === Swiper Section === */}
      <div className='relative group'>
        <Swiper
          modules={[Navigation, FreeMode, Mousewheel, Keyboard, Autoplay]}
          slidesPerView={"auto"}
          freeMode={true}
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          keyboard={{ enabled: true }}
          loop={loop}
          autoplay={
            autoplay
              ? { delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }
              : false
          }
          spaceBetween={12}
          breakpoints={breakpoints}
          slidesOffsetBefore={isMobile ? 16 : 0}
          slidesOffsetAfter={isMobile ? 16 : 0}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            // Attach refs before Swiper initializes
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;

            // Respect visitors who have opted out of motion. Disabled here,
            // before init, so autoplay never schedules its first slide.
            if (
              autoplay &&
              window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ) {
              swiper.params.autoplay.enabled = false;
            }
          }}
          className={`${title ? "mt-3 lg:mt-6" : ""} overflow-visible`}
        >
          {children.map((item, index) => (
            <SwiperSlide key={index} className='!w-auto !h-auto'>
              {item}
            </SwiperSlide>
          ))}
        </Swiper>

        {/* === Gradient Arrows (Desktop only) === */}
        {children.length > 1 && (
          <>
            <div className='absolute top-0 left-0 z-20 items-center hidden w-16 h-full transition duration-300 -translate-x-full opacity-0 lg:flex group-hover:translate-x-0 group-hover:opacity-100 bg-gradient-to-r from-background via-background/70 to-transparent'>
              <button
                type='button'
                ref={prevRef}
                className='pointer-events-auto'
              >
                <CaretLeft size={32} color='#fff' />
              </button>
            </div>

            <div className='absolute top-0 right-0 z-20 items-center justify-end hidden w-16 h-full pr-4 transition duration-300 translate-x-full opacity-0 lg:flex group-hover:translate-x-0 group-hover:opacity-100 bg-gradient-to-l from-background via-background/70 to-transparent'>
              <button
                type='button'
                ref={nextRef}
                className='pointer-events-auto'
              >
                <CaretRight size={32} color='#fff' />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Carousel;
